import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { OpenScreenId } from '@/generated/screens.g'
import router from '@/router'
import { routeForScreen, screenLabel } from '@/router/screens'
import type { StageChunk, StageDeclineReason } from '@/types/chat'

export interface StageDecision {
  readonly accepted: boolean
  readonly reason?: StageDeclineReason
  readonly detail?: string
}

export interface StageContext {
  readonly screen: OpenScreenId
  readonly itemId?: string
  readonly label: string
}

export const useStageStore = defineStore('stage', () => {
  const blockers = ref(new Map<string, string>())
  const context = ref<StageContext | null>(null)
  const returnTo = ref<string | null>(null)
  const lastDecline = ref<StageDecision | null>(null)
  const userBusy = ref(false)
  /** Set once a stage move from this turn has navigated, so a second chunk cannot race it; cleared when the next turn begins. */
  const turnNavigated = ref(false)

  const canReturn = computed(() => returnTo.value !== null)

  function block(id: string, reason: string): void {
    blockers.value = new Map(blockers.value).set(id, reason)
  }

  function unblock(id: string): void {
    const next = new Map(blockers.value)
    next.delete(id)
    blockers.value = next
  }

  function setUserBusy(busy: boolean): void {
    userBusy.value = busy
  }

  function decide(chunk: StageChunk): StageDecision {
    const [blockedReason] = blockers.value.values()
    if (blockedReason) return { accepted: false, reason: 'blocked', detail: blockedReason }

    if (userBusy.value) {
      return { accepted: false, reason: 'user-busy' }
    }

    if (turnNavigated.value) {
      return { accepted: false, reason: 'blocked' }
    }

    if (!routeForScreen(chunk.screen, chunk.itemId)) return { accepted: false, reason: 'no-screen' }

    return { accepted: true }
  }

  /** Acts on a stage request from the agent; the client may refuse, and a refused move is dropped. */
  function request(chunk: StageChunk): StageDecision {
    const decision = decide(chunk)
    if (!decision.accepted) {
      lastDecline.value = decision
      return decision
    }

    lastDecline.value = null
    // Only the first move of a run records where the user was.
    if (returnTo.value === null) returnTo.value = router.currentRoute.value.fullPath
    context.value = {
      screen: chunk.screen,
      ...(chunk.itemId ? { itemId: chunk.itemId } : {}),
      label: screenLabel(chunk.screen),
    }

    const route = routeForScreen(chunk.screen, chunk.itemId)
    if (route && router.resolve(route).fullPath !== router.currentRoute.value.fullPath) {
      void router.push(route)
    }

    turnNavigated.value = true

    return decision
  }

  /** Takes the user back to where the agent found them, as a pop so browser history stays consistent. */
  function back(): void {
    const target = returnTo.value
    if (!target) return
    returnTo.value = null
    context.value = null
    if (window.history.state?.back === target) {
      router.back()
      return
    }
    void router.push(target)
  }

  function clearContext(): void {
    context.value = null
    returnTo.value = null
  }

  function beginTurn(): void {
    turnNavigated.value = false
  }

  return {
    blockers,
    context,
    canReturn,
    lastDecline,
    block,
    unblock,
    setUserBusy,
    request,
    back,
    clearContext,
    beginTurn,
  }
})
